/** The activity category record's own inputs (plan §4 "One card", D3, O14), registered under
 *  the records core's names:
 *
 *  - `image` — the icon. Picking a file uploads it at once (`POST /media/staged/`, purpose
 *    `category_icon`, the category's operator — strict SVG is allowed server-side) and the
 *    draft holds the Image id the upload answers; the save attaches it. A save refused for an
 *    upload the daily sweep removed (`staged_image_gone`) offers "Upload it again". The icon
 *    is the records ImageWidget's 48 px `tile` — the card's view-mode tile, so pressing E
 *    moves nothing — and "No icon" without one.
 *  - `tags` (the catalog's `atags`, `coreWidget`) — the operator's access tags and its
 *    franchise parent's (catalog picker `atags`).
 *
 *  Create the set once per category (`useCategoryWidgets`): a new definition per render
 *  would remount the inputs. The stored icon is read at render time, so a save's re-seeded
 *  icon shows without remounting the input. */
import { useMemo, useRef } from 'react';

import type { ActivityCategoryDetail } from '@/api/activityCategories';
import { catalogPicker } from '@/api/catalogPickers';
import { stagedImageUploader } from '@/api/stagedImages';
import type { RecordWidget, RecordWidgetProps } from '@/features/records/editor/types';
import { ImageWidget, type StoredImage } from '@/features/records/fields/widgets/ImageWidget';
import { createTagsWidget } from '@/features/records/fields/widgets/pickers';

/** The icon field's words without an icon — in both modes. */
export const NO_ICON = 'No icon';

/** The category's widgets, bound to its operator. `storedIcon` reads the stored icon when
 *  the input renders. */
export function categoryWidgets(
  tenantId: number,
  storedIcon: () => StoredImage | null,
): Record<string, RecordWidget> {
  const upload = stagedImageUploader('category_icon', tenantId);
  function CategoryIconWidget(p: RecordWidgetProps) {
    return (
      <ImageWidget {...p} stored={storedIcon()} staging={{ kind: 'uploaded', upload }} shape="tile" emptyText={NO_ICON} />
    );
  }
  return {
    image: CategoryIconWidget,
    tags: createTagsWidget({
      load: async (_f, _r, q, signal) =>
        (await catalogPicker(tenantId, 'atags', { q, signal })).results.map((o) => ({
          id: o.id,
          label: o.name ?? `#${o.id}`,
          // The operator's own and its franchise parent's: the picker does not say which.
          group: 'Access tags',
        })),
      placeholder: 'Add an access tag',
      reserveAddRow: true,
    }),
  };
}

/** The widgets of one category — stable while its operator is. */
export function useCategoryWidgets(
  detail: Pick<ActivityCategoryDetail, 'tenant'>,
  storedIcon: StoredImage | null,
): Record<string, RecordWidget> {
  const icon = useRef(storedIcon);
  icon.current = storedIcon;
  const tenantId = detail.tenant.id;
  return useMemo(() => categoryWidgets(tenantId, () => icon.current), [tenantId]);
}
